import { Injectable, computed, signal } from '@angular/core';

const STORAGE_KEY = 'fc-emoji-recents';
/** Cap the list so the "Frequently Used" section stays a few rows tall. */
const MAX_RECENTS = 36;

interface RecentEntry {
  native: string;
  count: number;
  updated: number;
}

/**
 * Tracks the user's most-used emoji and persists them in `localStorage` so the
 * "Frequently Used" section survives reloads. Falls back to in-memory only when
 * storage is unavailable (private mode / quota).
 */
@Injectable({ providedIn: 'root' })
export class EmojiRecentsService {
  private readonly entries = signal<RecentEntry[]>(this.load());

  /** Glyphs ordered by usage (most-used first), then most-recent. */
  readonly recents = computed(() =>
    [...this.entries()]
      .sort((a, b) => b.count - a.count || b.updated - a.updated)
      .map((entry) => entry.native),
  );

  add(native: string): void {
    const now = Date.now();
    const previous = this.entries();
    const existing = previous.find((entry) => entry.native === native);
    const updated: RecentEntry = existing
      ? { native, count: existing.count + 1, updated: now }
      : { native, count: 1, updated: now };

    const next = [updated, ...previous.filter((e) => e.native !== native)]
      .sort((a, b) => b.count - a.count || b.updated - a.updated)
      .slice(0, MAX_RECENTS);

    this.entries.set(next);
    this.persist(next);
  }

  private load(): RecentEntry[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private persist(entries: RecentEntry[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      /* storage unavailable — recents stay in-memory for this session */
    }
  }
}
